import React, { FC, useEffect, useRef, useState } from 'react';
import { Icon } from '@payfit/midnight/dist/Icon';
import { Loader } from '@payfit/midnight/dist/Loader';
import { Pagination } from '@payfit/midnight/dist/Pagination';

import {
  DEBOUNCE_TIMEOUT,
  HIGHLIGHT,
  LABEL,
  NO_ITEM,
  RESULT_BY_PAGE,
  SearchResult,
  highlightMatches,
} from './logic';
import { classNames } from '../../utils/style';
import { useDebounce } from '../../utils/useDebounce';

const searchbarHeight = 48;
const gapResults = 8; // gap between searchbar and results panel

export interface SearchbarProps {
  slugs?: string[];
  locale: 'fr' | 'en' | 'es';
  searchFn: (qry: any, loc: any) => Promise<SearchResult[]>;
}

export const Searchbar: FC<SearchbarProps> = ({ slugs, locale, searchFn }) => {
  const inputRef = useRef<any>(null);
  const [query, setQuery] = useState<any>('');
  const [results, setResults] = useState<any>([]);
  const [page, setPage] = useState<any>(1);
  const [loading, setLoading] = useState<any>(false);
  const debouncedQuery = useDebounce(query, DEBOUNCE_TIMEOUT);

  const handleReset = (): void => {
    setQuery('');
    setResults([]);
    setPage(1);
  };
  const focusSearch = (): void => {
    if (inputRef?.current) inputRef?.current.focus();
  };

  useEffect(
    () => {
      setLoading(true);
      if (debouncedQuery) {
        setResults([]);
        searchFn(debouncedQuery, locale)
          .then((res) => {
            if (res) {
              slugs && setResults(getResultsInSlug(res, slugs));
              setLoading(false);
            }
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        setResults([]);
        setLoading(false);
      }
    },
    [debouncedQuery, slugs, searchFn, locale], // Only call effect if debounced query changes
  );

  return (
    <div
      className={classNames(
        'SearchBar flex relative justify-center w-full px-[10px]',
        'md:w-auto md:px-0',
        'md:m-[0px]',
      )}
    >
      <div className="relative w-[320px] lg:w-[405px]">
        <button
          className="hover:cursor-pointer block bg-lang-31"
          onClick={focusSearch}
          aria-label="search icon"
        >
          <Icon
            size={24}
            position={'absolute'}
            padding={12}
            paddingTop={13}
            left={13}
            top={1}
            src="search"
            color={'silver300'}
          />
        </button>
        <input
          ref={inputRef}
          type="text"
          placeholder={LABEL[locale]}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          className={classNames(
            `w-[320px] lg:w-[405px] border-neutral-200 focus:border-blue-101 border`,
            'rounded-[40px] text-sm leading-6 tracking-tight',
            'py-0 pr-56 pl-56 px-[12px] bg-lang-31 focus:bg-neutral-1',
          )}
          style={{ height: searchbarHeight }}
        ></input>
        <button
          className="hover:cursor-pointer block bg-lang-31"
          onClick={handleReset}
          aria-label="clear icon"
        >
          <Icon
            size={24}
            position={'absolute'}
            padding={12}
            paddingTop={13}
            right={13}
            top={1}
            src={'clear'}
            display={query ? 'inline-flex' : 'none'}
            color={'silver300'}
          />
        </button>
      </div>
      {query.length > 2 && (
        <div
          className={classNames(
            'SearchBarResult',
            'flex items-center absolute flex-col justify-between overflow-hidden',
            'bg-neutral-1 border border-neutral-200',
            `w-[320px] md:w-[405px] z-[1000] min-h-[380px] rounded-12`,
          )}
          style={{ top: searchbarHeight + gapResults }}
        >
          {!loading && (
            <div>
              {results
                .slice(
                  (page - 1) * RESULT_BY_PAGE,
                  (page - 1) * RESULT_BY_PAGE + RESULT_BY_PAGE,
                )
                .map((result: any) => (
                  <a
                    className="search-result block p-6 md:p-12 xl:p-18 pb-0 hover:bg-neutral-200 hover:cursor-pointer"
                    key={result.id}
                    href={result.url}
                  >
                    <div className="text-sm text-neutral-301 font-bold text-left">
                      {result.title}
                    </div>
                    <div
                      className="text-[10px] text-neutral-301 text-left"
                      dangerouslySetInnerHTML={{
                        __html:
                          highlightMatches(
                            result.highlight?.body?.length > 0
                              ? result.highlight?.body[0]
                              : '',
                            HIGHLIGHT,
                          ) + `...`,
                      }}
                    />
                  </a>
                ))}
            </div>
          )}
          {results.length === 0 && !loading && (
            <div className="text-neutral-101 text-center text-[14px]">
              {NO_ITEM(query)[locale]}
            </div>
          )}
          {loading && (
            <div className="flex justify-center mt-[160px] w-full">
              <Loader variation="blue" size={32} />
            </div>
          )}
          <div>
            <hr className="border-t border-neutral-101 opacity-50" />
            <Pagination
              onChange={(p) => {
                setPage(p);
              }}
              currentPage={page}
              totalPages={Math.ceil(results.length / RESULT_BY_PAGE)}
              margin={8}
            />
          </div>
        </div>
      )}
    </div>
  );
};

function getResultsInSlug(data: SearchResult[], slugs: string[]) {
  return data.filter((res) =>
    slugs ? slugs.some((slug) => res.url.includes(`/${slug}`)) : res,
  );
}
